import type { ConfigType } from '@plone/registry';
import { VoyagerSearchService } from '../components/Blocks/VoyagerSearchPageBlock/Services/VoyagerSearchService';
import { fetchDetailsBlockConfig } from '../components/Blocks/VoyagerSearchPageBlock/Services/detailsBlockConfig';
import searchSVG from '@plone/volto/icons/zoom.svg';
import mapSVG from '@plone/volto/icons/map.svg';
import folderSVG from '@plone/volto/icons/folder.svg';
import contentSVG from '@plone/volto/icons/content.svg';
import ExpandableTextareaWidget from '../components/manage/Widgets/ExpandableTextareaWidget';
import HierarchyTreeBaseWidget from '../components/manage/Widgets/HierarchyTreeBaseWidget';
import SidebarApiTestLinkWidget from '../components/manage/Widgets/SidebarApiTestLinkWidget';

// Voyager Search Page Block imports
import VoyagerSearchPageBlockView from '../components/Blocks/VoyagerSearchPageBlock/MetaDataSearchPageBlock';
import VoyagerSearchPageBlockEdit from '../components/Blocks/VoyagerSearchPageBlock/MetaDataSearchPageBlockEdit';
import VoyagerSearchPageBlockSchema from '../components/Blocks/VoyagerSearchPageBlock/MetaDataSearchPageBlockSchema';

// Voyager Search Details Page Block imports
import VoyagerSearchDetailsPageBlockView from '../components/Blocks/VoyagerSearchPageBlock/VoyagerSearchDetailsPageBlock';
import VoyagerSearchDetailsPageBlockEdit from '../components/Blocks/VoyagerSearchPageBlock/VoyagerSearchDetailsPageBlockEdit';
import VoyagerSearchDetailsPageBlockSchema from '../components/Blocks/VoyagerSearchPageBlock/VoyagerSearchDetailsPageBlockSchema';

// Voyager Search Bar Block imports
import VoyagerSearchBarBlockView from '../components/Blocks/VoyagerSearchBarBlock/VoyagerSearchBarBlock';
import VoyagerSearchBarBlockEdit from '../components/Blocks/VoyagerSearchBarBlock/VoyagerSearchBarBlockEdit';
import VoyagerSearchBarBlockSchema from '../components/Blocks/VoyagerSearchBarBlock/VoyagerSearchBarBlockSchema';

// Collections Block imports
import CollectionsBlockView from '../components/Blocks/CollectionsBlock/CollectionsBlock';
import CollectionsBlockEdit from '../components/Blocks/CollectionsBlock/CollectionsBlockEdit';
import CollectionsBlockSchema from '../components/Blocks/CollectionsBlock/CollectionsBlockSchema';

// Featured Block imports
import FeaturedBlockView from '../components/Blocks/FeaturesBlock/FeaturedBlock';
import FeaturedBlockEdit from '../components/Blocks/FeaturesBlock/FeaturedBlockEdit';
import FeaturedBlockSchema from '../components/Blocks/FeaturesBlock/FeaturedBlockSchema';

// Voyager Enhanced Search Block imports
import VoyagerEnhancedSearchView from '../components/Blocks/VoyagerEnhancedSearch/VoyagerEnhancedSearch';
import VoyagerEnhancedSearchEdit from '../components/Blocks/VoyagerEnhancedSearch/VoyagerEnhancedSearchEdit';
import VoyagerEnhancedSearchSchema from '../components/Blocks/VoyagerEnhancedSearch/VoyagerEnhancedSearchSchema';


const blocksConfig = {
  voyagersearchpage: {
    id: 'voyagersearchpage',
    title: 'Voyager Search Page',
    icon: searchSVG,
    group: 'common',
    view: VoyagerSearchPageBlockView,
    edit: VoyagerSearchPageBlockEdit,
    blockSchema: VoyagerSearchPageBlockSchema,
    restricted: false,
    mostUsed: false,
  },
  voyagersearchdetailspage: {
    id: 'voyagersearchdetailspage',
    title: 'Voyager Search Details Page',
    icon: searchSVG,
    group: 'common',
    view: VoyagerSearchDetailsPageBlockView,
    edit: VoyagerSearchDetailsPageBlockEdit,
    blockSchema: VoyagerSearchDetailsPageBlockSchema,
    restricted: false,
    mostUsed: false,
  },
  voyagersearchbar: {
    id: 'voyagersearchbar',
    title: 'Voyager Search Bar',
    icon: mapSVG,
    group: 'common',
    view: VoyagerSearchBarBlockView,
    edit: VoyagerSearchBarBlockEdit,
    blockSchema: VoyagerSearchBarBlockSchema,
    restricted: false,
    mostUsed: false,
  },
  collections: {
    id: 'collections',
    title: 'Collections',
    icon: folderSVG,
    group: 'common',
    view: CollectionsBlockView,
    edit: CollectionsBlockEdit,
    blockSchema: CollectionsBlockSchema,
    restricted: false,
    mostUsed: false,
  },
  featured: {
    id: 'featured',
    title: 'Featured',
    icon: contentSVG,
    group: 'common',
    view: FeaturedBlockView,
    edit: FeaturedBlockEdit,
    blockSchema: FeaturedBlockSchema,
    restricted: false,
    mostUsed: false,
  },
  voyagerenhancedsearch: {
    id: 'voyagerenhancedsearch',
    title: 'Enhanced Search (AI Chat)',
    icon: searchSVG,
    group: 'common',
    view: VoyagerEnhancedSearchView,
    edit: VoyagerEnhancedSearchEdit,
    blockSchema: VoyagerEnhancedSearchSchema,
    restricted: false,
    mostUsed: false,
  },
};

// Pre-fetches the Voyager item on the server for the dataset detail page so that
// the Dataset JSON-LD is present in the initial SSR HTML (not added after hydration).
// The fetched item is stored at state.reduxAsyncConnect.voyagerItemDetails.
//
// The path is a pattern, not a literal, so ONE extender covers every agency's
// details page - /doi-search/doi-search-details, /usgs-search/usgs-search-details
// and so on. Volto matches this with react-router-config's matchRoutes, so
// path-to-regexp syntax applies.
//
// Which Voyager instance to query is read from the page's own block config at
// request time (see detailsBlockConfig.ts). That keeps agencies configurable as
// content: add a search page in Volto and SSR follows, no rebuild.
const voyagerItemDetailsExtender = {
  path: '/:searchRoot(.*-search)/:detailsPage(.*-search-details)',
  exact: true,
  extend: (dispatchActions: any[]) => {
    if (dispatchActions.some((a: any) => a.key === 'voyagerItemDetails')) {
      return dispatchActions;
    }
    dispatchActions.push({
      key: 'voyagerItemDetails',
      promise: async ({
        location,
      }: {
        location: { pathname: string; search: string };
      }) => {
        if (typeof __SERVER__ === 'undefined' || !__SERVER__) return null;
        const params = new URLSearchParams(location.search);
        const itemId = params.get('id') || params.get('itemId');
        if (!itemId) return null;

        // Falls back to environment defaults if the lookup fails, which is the
        // behaviour this had before it was made per-page. The config is passed
        // per call rather than via configure(), which on the server would be
        // shared across concurrent requests.
        const blockConfig = await fetchDetailsBlockConfig(location.pathname);
        return VoyagerSearchService.fetchItemDetails(itemId, {
          baseUrl: blockConfig.voyagerBaseUrl,
          configId: blockConfig.voyagerConfigId,
        });
      },
    });
    return dispatchActions;
  },
};

export default function install(config: ConfigType) {
  config.widgets = config.widgets || {};
  config.widgets.widget = config.widgets.widget || {};
  config.widgets.id = config.widgets.id || {};
  config.widgets.widget.expandable_textarea = ExpandableTextareaWidget;
  config.widgets.id.expandable_textarea = ExpandableTextareaWidget;
  config.widgets.widget.hierarchy_tree_base = HierarchyTreeBaseWidget;
  config.widgets.id.hierarchy_tree_base = HierarchyTreeBaseWidget;
  config.widgets.widget.sidebar_api_test_link = SidebarApiTestLinkWidget;
  config.widgets.id.sidebar_api_test_link = SidebarApiTestLinkWidget;

  config.blocks.blocksConfig.voyagersearchpage = blocksConfig.voyagersearchpage;
  config.blocks.blocksConfig.voyagersearchdetailspage =
    blocksConfig.voyagersearchdetailspage;
  config.blocks.blocksConfig.voyagersearchbar = blocksConfig.voyagersearchbar;
  config.blocks.blocksConfig.collections = blocksConfig.collections;
  config.blocks.blocksConfig.featured = blocksConfig.featured;
  config.blocks.blocksConfig.voyagerenhancedsearch =
    blocksConfig.voyagerenhancedsearch;

  config.settings.asyncPropsExtenders = [
    ...(config.settings.asyncPropsExtenders || []),
    voyagerItemDetailsExtender,
  ];

  return config;
}
